import { useEffect, useMemo, useState } from 'react';
import {
  Building2,
  Clock,
  CopyCheck,
  Download,
  FileText,
  Gauge,
  GitMerge,
  Info,
  PackageSearch,
  PanelRightOpen,
  SearchCheck,
  SearchX,
  Tag,
  Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EmptyState } from '@/components/ui/empty-state';
import { BulkBar } from '@/components/ui/bulk-bar';
import { RowActions } from '@/components/ui/row-actions';
import { getErrorMessage } from '@/lib/errors';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  ChipSelect,
  CountPill,
  ExportMenu,
  InvCheckbox,
  SearchField,
  TableFoot,
  TableScroll,
  TableWrap,
  numClass,
  tableClass,
  tdClass,
  thClass
} from './InventoryUi';
import { EMPTY_FILTER_VALUE, duplicateReviewGroups, itemDataIssue, partKey, plural, samePartNumbers, toQty } from './inventoryModel';
import { SuggestionChip, SuggestionsCallout, SuggestionsDialog } from './CatalogSuggestions';

export const CATALOG_HEALTH = [
  { key: 'duplicates', label: 'Possible duplicates', icon: CopyCheck, countKey: 'duplicate_groups', unit: (count) => (count === 1 ? 'group' : 'groups'), tone: 'amber' },
  { key: 'no_category', label: 'No category', icon: Tag, countKey: 'no_category', tone: 'amber' },
  { key: 'no_description', label: 'No description', hint: 'No description, or a part number such as - or 10"', icon: FileText, countKey: 'no_description', tone: 'amber' },
  { key: 'no_lead_time', label: 'No lead time', icon: Clock, countKey: 'no_lead_time', tone: 'gray' },
  { key: 'no_supplier', label: 'No supplier', icon: Building2, countKey: 'no_supplier', tone: 'gray' },
  { key: 'no_min_level', label: 'No min level', hint: 'Low stock uses 5 until a min level is set', icon: Gauge, countKey: 'no_min_level', tone: 'gray' }
];

const facetToOptions = (entries = [], emptyLabel) => entries.map((entry) => ({
  value: entry.value,
  label: entry.value === EMPTY_FILTER_VALUE ? emptyLabel : (entry.label || entry.value),
  count: entry.count,
  muted: entry.value === EMPTY_FILTER_VALUE
}));

function pageFacet(items, field, emptyLabel) {
  const counts = new Map();
  items.forEach((item) => {
    const value = String(item[field] || '').trim() || EMPTY_FILTER_VALUE;
    counts.set(value, (counts.get(value) || 0) + 1);
  });
  return [...counts.entries()].map(([value, count]) => ({ value, count, label: value === EMPTY_FILTER_VALUE ? emptyLabel : value }));
}

export default function CatalogTab({
  catalogItems = [],
  catalogTotal = 0,
  catalogMeta = null,
  catalogPage,
  catalogTotalPages,
  setCatalogPage,
  catalogLimit = 50,
  onCatalogLimitChange,
  catalogLoading = false,
  search = '',
  onSearchChange,
  brandFilter = null,
  onBrandFilterChange,
  categoryFilter = null,
  onCategoryFilterChange,
  supplierFilter = null,
  onSupplierFilterChange,
  healthFilter = null,
  onHealthFilterChange,
  onExport,
  onExportSelected,
  selectedCatalogIds,
  onClearSelection,
  canDelete = false,
  canMerge = false,
  canEdit = true,
  handleDeleteSelectedCatalogItems,
  allCatalogSelected,
  handleToggleAllCatalogSelection,
  toggleCatalogSelection,
  duplicateGroups,
  duplicatesDialogOpen,
  setDuplicatesDialogOpen,
  duplicatesLoading,
  handleCheckCatalogDuplicates,
  onMergeGroup,
  selectedCatalogItems,
  mergeDialogOpen,
  setMergeDialogOpen,
  mergeForm,
  handleOpenMergeDialog,
  handleMergeFormChange,
  handleMergeCatalogItems,
  duplicateKeys = new Map(),
  openItemId = null,
  onOpenItem,
  onBulkSetField,
  onApplySuggestions
}) {
  const [fieldDialog, setFieldDialog] = useState(null); // 'category' | 'brand'
  // Smart catalog (20 #8): suggestions come with the whole-catalog counts (catalogMeta.suggestions).
  const suggestions = catalogMeta?.suggestions;
  const suggestionById = useMemo(() => new Map((suggestions?.items || []).map((item) => [item.id, item])), [suggestions]);
  const [suggestDialog, setSuggestDialog] = useState({ open: false, focusId: null });
  const catalogSize = catalogMeta?.catalog_total ?? catalogTotal;
  const facets = catalogMeta?.facets;
  const brandOptions = facetToOptions(facets?.brands || pageFacet(catalogItems, 'brand', 'No brand'), 'No brand');
  const categoryOptions = facetToOptions(facets?.categories || pageFacet(catalogItems, 'category', 'No category'), 'No category');
  const supplierOptions = facetToOptions(facets?.suppliers || pageFacet(catalogItems, 'preferred_supplier', 'No supplier'), 'No supplier');
  const health = catalogMeta?.health;
  const hasFilters = Boolean(search || brandFilter || categoryFilter || supplierFilter || healthFilter);
  const pageSelectedCount = catalogItems.filter((item) => selectedCatalogIds.includes(item.id)).length;
  const somePageSelected = pageSelectedCount > 0 && !allCatalogSelected;
  const start = catalogTotal ? (catalogPage - 1) * catalogLimit + 1 : 0;
  const end = Math.min(catalogTotal, catalogPage * catalogLimit);
  const selectedCount = selectedCatalogIds.length;

  const clearFilters = () => {
    onSearchChange?.('');
    onBrandFilterChange?.(null);
    onCategoryFilterChange?.(null);
    onSupplierFilterChange?.(null);
    onHealthFilterChange?.(null);
  };

  const fieldOptions = (fieldDialog === 'brand' ? brandOptions : categoryOptions)
    .filter((option) => option.value !== EMPTY_FILTER_VALUE)
    .map((option) => option.label);

  return (
    <div className="space-y-4" data-testid="inventory-catalog-tab">
      {/* Toolbar — every filter runs on the server across the whole catalog */}
      <div className="flex flex-wrap items-center gap-2">
        <SearchField value={search} onChange={onSearchChange} placeholder="Part no., description, brand…" className="w-full sm:w-[250px]" label="Search catalog" />
        <ChipSelect label="Brand" value={brandFilter} options={brandOptions} onChange={onBrandFilterChange} testId="catalog-chip-brand" />
        <ChipSelect label="Category" value={categoryFilter} options={categoryOptions} onChange={onCategoryFilterChange} testId="catalog-chip-category" />
        <ChipSelect label="Supplier" value={supplierFilter} options={supplierOptions} onChange={onSupplierFilterChange} testId="catalog-chip-supplier" />
        <span className="inline-flex items-center gap-1.5 text-[12.5px] text-ink-3">
          <Info className="h-3.5 w-3.5" aria-hidden="true" />
          Filters cover all {formatNumber(catalogSize)} items, not just this page
        </span>
        <span className="hidden flex-1 xl:block" />
        <Button
          variant="outline"
          size="sm"
          className="text-ink"
          onClick={handleCheckCatalogDuplicates}
          disabled={duplicatesLoading}
          data-testid="check-catalog-duplicates-btn"
        >
          <SearchCheck aria-hidden="true" /> {duplicatesLoading ? 'Checking…' : 'Find duplicates'}
        </Button>
        <ExportMenu
          testId="catalog-export-btn"
          disabled={!catalogTotal}
          onExcel={() => onExport?.('xlsx')}
          onCsv={() => onExport?.('csv')}
        />
      </div>

      {/* Data health — counts across the whole catalog; each one filters the table */}
      {health ? (
        <div className="flex flex-wrap items-center gap-2" data-testid="catalog-data-health">
          <span className="mr-1 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Data health</span>
          {CATALOG_HEALTH.map((entry) => {
            const count = toQty(health[entry.countKey]);
            const active = healthFilter === entry.key;
            const Icon = entry.icon;
            return (
              <button
                key={entry.key}
                type="button"
                aria-pressed={active}
                onClick={() => onHealthFilterChange?.(active ? null : entry.key)}
                data-testid={`catalog-health-${entry.key}`}
                title={entry.hint}
                className={cn(
                  'inline-flex h-[30px] items-center gap-1.5 rounded-full border px-2.5 text-[12.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  active
                    ? 'border-[color:var(--brand-soft-border)] bg-brand-soft text-brand-ink'
                    : 'border-line bg-surface text-ink-2 hover:bg-surface-hover hover:text-ink'
                )}
              >
                <Icon className="h-[13px] w-[13px]" aria-hidden="true" />
                {entry.label}
                <CountPill tone={count ? entry.tone : 'green'}>
                  {formatNumber(count)}{entry.unit ? ` ${entry.unit(count)}` : ''}
                </CountPill>
              </button>
            );
          })}
        </div>
      ) : null}

      <SuggestionsCallout suggestions={suggestions} canEdit={canEdit} onReview={() => setSuggestDialog({ open: true, focusId: null })} />

      <TableWrap data-testid="catalog-table" aria-busy={catalogLoading}>
        <TableScroll>
          <table className={cn(tableClass, 'min-w-[860px] table-fixed')}>
            <colgroup>
              <col className="w-11" />
              <col className="w-[190px]" />
              <col />
              <col className="w-[64px]" />
              <col className="w-[88px]" />
              <col className="w-[96px]" />
              <col className="w-[160px]" />
              <col className="w-11" />
            </colgroup>
            <thead>
              <tr>
                <th className={thClass}>
                  <InvCheckbox
                    checked={allCatalogSelected}
                    indeterminate={somePageSelected}
                    onCheckedChange={() => handleToggleAllCatalogSelection(!allCatalogSelected)}
                    aria-label="Select all items on this page"
                  />
                </th>
                <th className={cn(thClass, 'text-ink')}>Part no. ↑</th>
                <th className={thClass}>Description <span className="font-semibold text-ink-4">· Brand · Category</span></th>
                <th className={thClass}>Unit</th>
                <th className={cn(thClass, numClass)}>Min level</th>
                <th className={cn(thClass, numClass)}>Lead time</th>
                <th className={thClass}>Supplier</th>
                <th className={thClass}><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className={cn(catalogLoading && 'opacity-60')}>
              {catalogItems.map((item) => {
                const selected = selectedCatalogIds.includes(item.id);
                const isOpen = openItemId === item.id;
                const noCategory = !String(item.category || '').trim();
                const issue = itemDataIssue(item);
                const minLevel = toQty(item.min_stock_level);
                const leadTime = toQty(item.lead_time_days);
                const duplicates = duplicateKeys.get(partKey(item.part_number));
                return (
                  <tr
                    key={item.id}
                    onClick={() => onOpenItem?.(item)}
                    className={cn(
                      'cursor-pointer',
                      selected || isOpen ? '[&>td]:bg-brand-soft' : '[&:hover>td]:bg-surface-hover',
                      isOpen && '[&>td:first-child]:shadow-[inset_3px_0_0_var(--brand)]'
                    )}
                    data-testid={`catalog-row-${item.id}`}
                    aria-current={isOpen ? 'true' : undefined}
                  >
                    <td className={tdClass} onClick={(event) => event.stopPropagation()}>
                      <InvCheckbox
                        checked={selected}
                        onCheckedChange={(checked) => toggleCatalogSelection(item.id, Boolean(checked), item)}
                        aria-label={`Select ${item.part_number}`}
                      />
                    </td>
                    <td className={cn(tdClass, 'py-1.5')}>
                      <button
                        type="button"
                        onClick={(event) => { event.stopPropagation(); onOpenItem?.(item); }}
                        className="block max-w-full truncate rounded text-left font-mono text-[12.5px] text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        title={`Open ${item.part_number}`}
                      >
                        {item.part_number}
                      </button>
                      {duplicates && duplicates.length > 1 ? (
                        <span
                          className="mt-0.5 inline-flex h-[18px] items-center gap-1 rounded-full bg-status-amber-bg px-1.5 text-[11px] font-bold text-status-amber-ink"
                          title={samePartNumbers(duplicates) ? 'The same part number appears more than once' : `Part numbers match ignoring - and spaces: ${duplicates.map((other) => other.part_number).join(' · ')}`}
                          data-testid={`catalog-duplicate-${item.id}`}
                        >
                          <CopyCheck className="h-[11px] w-[11px]" aria-hidden="true" /> {samePartNumbers(duplicates) ? 'Duplicate' : 'Similar part no.'} ×{duplicates.length}
                        </span>
                      ) : issue && issue !== 'No description' ? <span className="block text-[11px] text-status-amber-ink">{issue}</span> : null}
                    </td>
                    <td className={cn(tdClass, 'py-1.5')}>
                      <span className={cn('block truncate text-[13px]', item.description ? 'text-ink' : 'text-status-amber-ink')} title={item.description}>
                        {item.description || 'No description'}
                      </span>
                      <span className="mt-px flex min-w-0 items-center text-[11.5px] text-ink-3">
                        <span className="truncate">{item.brand || 'No brand'} · {noCategory ? <span className="text-status-amber-ink">No category</span> : item.category}</span>
                        {suggestionById.get(item.id) ? (
                          <SuggestionChip suggestion={suggestionById.get(item.id)} onClick={canEdit ? () => setSuggestDialog({ open: true, focusId: item.id }) : undefined} />
                        ) : null}
                      </span>
                    </td>
                    <td className={cn(tdClass, 'text-ink-2')}>{item.unit || 'pcs'}</td>
                    <td className={cn(tdClass, numClass)}>{minLevel > 0 ? formatNumber(minLevel) : <span className="text-ink-4" aria-label="Not set">—</span>}</td>
                    <td className={cn(tdClass, numClass)}>{leadTime > 0 ? `${formatNumber(leadTime)} d` : <span className="text-[12px] text-ink-3">Not set</span>}</td>
                    <td className={cn(tdClass, 'truncate')}>{item.preferred_supplier ? <span className="text-ink-2">{item.preferred_supplier}</span> : <span className="text-ink-4" aria-label="No supplier">—</span>}</td>
                    <td className={cn(tdClass, 'pl-0 text-right')} onClick={(event) => event.stopPropagation()}>
                      <RowActions
                        size="sm"
                        label={`Actions for ${item.part_number}`}
                        className="opacity-60 hover:opacity-100 focus-visible:opacity-100"
                        items={[
                          { label: canEdit ? 'Edit details…' : 'Open details', icon: PanelRightOpen, onSelect: () => onOpenItem?.(item) },
                          duplicates && duplicates.length > 1 && canMerge
                            ? { label: `Merge ${duplicates.length} duplicates…`, icon: GitMerge, onSelect: () => onMergeGroup?.(duplicates) }
                            : null,
                          canDelete ? { label: 'Delete item…', icon: Trash2, danger: true, onSelect: () => handleDeleteSelectedCatalogItems([item.id]) } : null
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableScroll>
        {!catalogItems.length && !catalogLoading ? (
          <EmptyState
            icon={hasFilters ? SearchX : PackageSearch}
            title={hasFilters ? 'No catalog items match' : 'The catalog is empty'}
            text={hasFilters ? 'Filters run over the whole catalog — try removing one.' : 'Use “Add items” to add part numbers.'}
            action={hasFilters ? <Button size="sm" variant="outline" className="text-ink" onClick={clearFilters}>Clear filters</Button> : null}
          />
        ) : null}
        <TableFoot
          left={(
            <span data-testid="catalog-range">
              {formatNumber(start)}–{formatNumber(end)} of <b className="font-semibold text-ink-2">{formatNumber(catalogTotal)}</b>
              {hasFilters && catalogSize !== catalogTotal ? <> · filtered from {formatNumber(catalogSize)}</> : null}
            </span>
          )}
          page={catalogPage}
          pageCount={catalogTotalPages}
          onPage={(next) => setCatalogPage(Math.min(Math.max(1, next), catalogTotalPages))}
          pageSize={catalogLimit}
          onPageSize={onCatalogLimitChange}
        />
      </TableWrap>

      <BulkBar
        count={selectedCount}
        onClear={onClearSelection}
        // The item drawer (460px, not modal) covers the right of the page: centre the bar over what is left.
        className={openItemId ? 'sm:pr-[444px]' : undefined}
        testId="catalog-bulkbar"
        actions={[
          {
            label: 'Merge…',
            icon: GitMerge,
            onSelect: handleOpenMergeDialog,
            disabled: !canMerge || selectedCount < 2,
            title: !canMerge ? 'Only Admin, Super Admin and Storekeeper can merge items' : selectedCount < 2 ? 'Select at least two items to merge' : undefined,
            testId: 'merge-selected-catalog-btn'
          },
          { label: 'Set category', icon: Tag, onSelect: () => setFieldDialog('category'), disabled: !canEdit, testId: 'catalog-bulk-category' },
          { label: 'Set brand', icon: Building2, onSelect: () => setFieldDialog('brand'), disabled: !canEdit, testId: 'catalog-bulk-brand' },
          { label: 'Export', icon: Download, onSelect: () => onExportSelected?.(), testId: 'catalog-bulk-export' }
        ]}
        moreActions={canDelete ? [{
          label: `Delete ${plural(selectedCount, 'item')}…`,
          icon: Trash2,
          danger: true,
          confirm: false, // the page asks with the full consequences and a typed count
          hint: 'type to confirm',
          onSelect: () => handleDeleteSelectedCatalogItems(selectedCatalogIds),
          testId: 'catalog-bulk-delete'
        }] : []}
      />

      <BulkFieldDialog
        field={fieldDialog}
        count={selectedCount}
        options={fieldOptions}
        onOpenChange={(open) => { if (!open) setFieldDialog(null); }}
        onSave={async (value) => { await onBulkSetField?.(fieldDialog, value); setFieldDialog(null); }}
      />

      <SuggestionsDialog
        open={suggestDialog.open}
        focusId={suggestDialog.focusId}
        onOpenChange={(open) => setSuggestDialog((current) => ({ ...current, open }))}
        suggestions={suggestions}
        onApply={async (rows) => { await onApplySuggestions?.(rows); }}
      />

      <DuplicatesReviewDialog
        open={duplicatesDialogOpen}
        onOpenChange={setDuplicatesDialogOpen}
        exactGroups={[...duplicateKeys.values()]}
        similarGroups={duplicateGroups}
        canMerge={canMerge}
        onMergeGroup={onMergeGroup}
      />

      <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-ink">Merge catalog items</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl border border-line bg-surface-2 p-3">
              <p className="mb-2 text-[12px] text-ink-3">{plural(selectedCatalogItems.length, 'selected item')}</p>
              <div className="space-y-1">
                {selectedCatalogItems.map((item) => (
                  <p key={item.id} className="text-[12.5px] text-ink">
                    <span className="font-mono">{item.part_number}</span>
                    <span className="text-ink-3"> — {item.description || '—'}</span>
                  </p>
                ))}
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-[12px] text-ink-3">Surviving item</Label>
                <Select value={mergeForm.survivor_item_id || ''} onValueChange={(value) => handleMergeFormChange({ survivor_item_id: value })}>
                  <SelectTrigger><SelectValue placeholder="Choose item" /></SelectTrigger>
                  <SelectContent>
                    {selectedCatalogItems.map((item) => (
                      <SelectItem key={item.id} value={item.id}>{item.part_number}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[12px] text-ink-3">Final part number</Label>
                <Input value={mergeForm.final_part_number || ''} onChange={(event) => handleMergeFormChange({ final_part_number: event.target.value })} className="font-mono" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[12px] text-ink-3">Final description</Label>
              <Input value={mergeForm.final_description || ''} onChange={(event) => handleMergeFormChange({ final_description: event.target.value })} />
            </div>
            <div className="rounded-xl border border-[color:var(--status-amber-bg)] bg-status-amber-bg p-3 text-[12.5px] text-status-amber-ink">
              Confirming will delete the duplicate catalog rows, update BOQ references across projects, and combine stock quantities into the surviving item.
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setMergeDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleMergeCatalogItems}><GitMerge aria-hidden="true" /> Confirm merge</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Bulk "Set category" / "Set brand" (Catalog idea 6) — one value for every selected item. */
function BulkFieldDialog({ field, count, options = [], onOpenChange, onSave }) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (field) { setValue(''); setError(''); } }, [field]);
  const label = field === 'brand' ? 'Brand' : 'Category';
  const submit = async (event) => {
    event.preventDefault();
    if (!value.trim()) { setError(`Type or pick a ${label.toLowerCase()}`); return; }
    setBusy(true);
    setError('');
    try {
      await onSave(value.trim());
    } catch (err) {
      setError(getErrorMessage(err, `Could not set the ${label.toLowerCase()}`));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={Boolean(field)} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-ink">Set {label.toLowerCase()}</DialogTitle>
          <DialogDescription className="text-[13px] text-ink-2">For the {plural(count, 'selected item')} — on every page of the selection.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="catalog-bulk-field-form">
          <label className="block">
            <span className="mb-1 block text-[12px] font-semibold text-ink-3">{label}</span>
            <Input value={value} onChange={(event) => setValue(event.target.value)} list="catalog-bulk-field-options" autoFocus placeholder={field === 'brand' ? 'e.g. Schneider' : 'e.g. Duct Sensors'} data-testid="catalog-bulk-field-input" />
            <datalist id="catalog-bulk-field-options">{options.map((option) => <option key={option} value={option} />)}</datalist>
          </label>
          {error ? <p className="text-[12.5px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="catalog-bulk-field-save">{busy ? 'Saving…' : `Set ${label.toLowerCase()}`}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Possible duplicates (Catalog idea 4): groups with a survivor radio, the items to fold in, and
 * "Merge group" — merged by id, so items on other pages are included. Same-part-number groups come
 * ticked; groups that only look alike (similar descriptions) start with just the kept item, so
 * different parts are never merged without a deliberate tick.
 */
function DuplicatesReviewDialog({ open, onOpenChange, exactGroups = [], similarGroups = [], canMerge, onMergeGroup }) {
  const [survivors, setSurvivors] = useState({});
  const [picked, setPicked] = useState({});
  const [busyGroup, setBusyGroup] = useState(null);
  useEffect(() => { if (open) { setSurvivors({}); setPicked({}); } }, [open]);

  const groups = useMemo(() => duplicateReviewGroups(exactGroups, similarGroups), [exactGroups, similarGroups]);

  const survivorOf = (group) => survivors[group.key] || group.items[0]?.id;
  const isIncluded = (group, item) => {
    if (item.id === survivorOf(group)) return true;
    const value = picked[`${group.key}:${item.id}`];
    return value === undefined ? group.kind === 'exact' : value;
  };
  const merge = async (group) => {
    const included = group.items.filter((item) => isIncluded(group, item));
    setBusyGroup(group.key);
    try {
      await onMergeGroup?.(included, survivorOf(group));
    } finally {
      setBusyGroup(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-ink">Catalog duplicates</DialogTitle>
          <DialogDescription className="text-[13px] text-ink-2">
            Pick the item to keep in each group and tick what folds into it — stock, bookings, movements and BOQ lines move onto the kept item.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {groups.length ? groups.map((group, index) => {
            const survivorId = survivorOf(group);
            const included = group.items.filter((item) => isIncluded(group, item));
            return (
              <div key={group.key} className="rounded-xl border border-line bg-surface-2 p-3" data-testid={`duplicate-group-${index}`}>
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Group {index + 1} · {plural(group.items.length, 'item')}</p>
                  <span className={cn(
                    'rounded-full px-2 py-px text-[11px] font-bold',
                    group.kind === 'exact' ? 'bg-status-amber-bg text-status-amber-ink' : 'bg-[color:var(--status-gray-bg)] text-ink-2'
                  )} data-testid={`duplicate-group-kind-${index}`}>
                    {group.kind === 'exact'
                      ? 'Same part number'
                      : group.kind === 'normalized'
                        ? 'Part numbers match ignoring - and spaces — tick only real duplicates'
                        : 'Similar description — tick only real duplicates'}
                  </span>
                  <span className="flex-1" />
                  {canMerge ? (
                    <Button type="button" size="xs" onClick={() => merge(group)} disabled={included.length < 2 || busyGroup === group.key} data-testid={`merge-group-${index}`}>
                      <GitMerge aria-hidden="true" /> {busyGroup === group.key ? 'Merging…' : included.length < 2 ? 'Tick items to merge' : `Merge ${included.length} items`}
                    </Button>
                  ) : <span className="text-[11.5px] text-ink-3">Admin, Super Admin or Storekeeper can merge</span>}
                </div>
                <table className={tableClass}>
                  <thead>
                    <tr>
                      <th className={cn(thClass, 'static w-[60px] xl:static')}>Keep</th>
                      <th className={cn(thClass, 'static w-[60px] xl:static')}>Merge</th>
                      <th className={cn(thClass, 'static xl:static')}>Part no.</th>
                      <th className={cn(thClass, 'static xl:static')}>Description</th>
                      <th className={cn(thClass, 'static xl:static')}>Brand · Category</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.items.map((item) => {
                      const isSurvivor = item.id === survivorId;
                      return (
                        <tr key={item.id}>
                          <td className={cn(tdClass, 'h-9')}>
                            <input
                              type="radio"
                              name={`survivor-${group.key}`}
                              checked={isSurvivor}
                              onChange={() => setSurvivors((current) => ({ ...current, [group.key]: item.id }))}
                              aria-label={`Keep ${item.part_number}`}
                              className="h-4 w-4 accent-[color:var(--brand)]"
                            />
                          </td>
                          <td className={cn(tdClass, 'h-9')}>
                            <InvCheckbox
                              checked={isIncluded(group, item)}
                              disabled={isSurvivor}
                              onCheckedChange={(checked) => setPicked((current) => ({ ...current, [`${group.key}:${item.id}`]: Boolean(checked) }))}
                              aria-label={`Merge ${item.part_number}`}
                            />
                          </td>
                          <td className={cn(tdClass, 'h-9 whitespace-nowrap font-mono text-[12.5px]')}>{item.part_number}</td>
                          <td className={cn(tdClass, 'h-9 text-ink-2')}>{item.description || '—'}</td>
                          <td className={cn(tdClass, 'h-9 text-ink-2')}>{[item.brand, item.category].filter(Boolean).join(' · ') || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          }) : (
            <EmptyState icon={CopyCheck} title="No similar catalog items found" text="Part numbers and descriptions look unique." />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
